import { useContext, useEffect, useState } from "react";
import { StyledQuoteItemForm } from "./QuoteItemForm.styled";
import {
  DocumentContext,
  LanguageContext,
} from "../../globals/LanguageContext";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  InputAdornment,
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { QuoteItem } from "../../models/QuoteItem";
import { useMaterial } from "../../stores/useMaterial";
import { Material } from "../../models/Material";
import { useProfile } from "../../stores/useProfile";
import { PlusIcon, TrashSimple, TrashSimpleIcon } from "@phosphor-icons/react";
import { AddQuoteItemDto } from "../../models/dto/QuoteDTO";
import { QuoteItemMaterial } from "../../models/dto/QuoteItemMaterial";
import { TextArea } from "../textArea/TextArea";

type QuoteItemFormProps = {
  quoteItem?: QuoteItem;
  onSave: (quoteItem: AddQuoteItemDto) => void;
  onUpdate: (quoteItemId: number, quoteItem: AddQuoteItemDto) => void;
};

export const QuoteItemForm = ({
  quoteItem,
  onSave,
  onUpdate,
}: QuoteItemFormProps) => {
  const { dictionary } = useContext(LanguageContext);
  const { documentDictionary } = useContext(DocumentContext);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<
    QuoteItemMaterial[]
  >([]);
  const [currentMaterial, setCurrentMaterial] = useState<Material | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [description, setDescription] = useState<string>("");
  const { fetchMaterials } = useMaterial();
  const { user } = useProfile();
  const {
    handleSubmit,
    control,
    register,
    watch,
    formState: { errors },
  } = useForm<AddQuoteItemDto>({
    defaultValues: {
      materialPercentage:
        Number(Number(quoteItem?.materialPercentage).toFixed(0)) || 30,
      vat: Number(Number(quoteItem?.vat).toFixed(0)) || 21,
      hourRate: Number(Number(quoteItem?.hourRate).toFixed(2)) || 55,
      plannedHours: Number(Number(quoteItem?.plannedHours).toFixed(2)) || 0,
      includeInTotal: quoteItem?.includeInTotal || true,
      extraAmount: Number(Number(quoteItem?.extraAmount).toFixed(2)) || 0,
    },
  });

  useEffect(() => {
    if (!user) return;
    fetchMaterials(user).then((mat) => {
      mat && setMaterials(mat);
    });
  }, [user]);

  useEffect(() => {
    if (!quoteItem || !quoteItem.materials) {
      setSelectedMaterials([]);
      return;
    }

    setSelectedMaterials(quoteItem.materials);
  }, [quoteItem]);

  const handleAddMaterial = () => {
    if (!currentMaterial) return;

    const materialId = currentMaterial.id;
    console.log(materialId)
    if (!materialId) return;

    setSelectedMaterials((prev) => {
      const existing = prev.find((m) => m.material?.id === materialId);

      if (existing) {
        return prev.map((m) =>
          m.material?.id === materialId ? { ...m, quantity: m.quantity + amount } : m
        );
      }

      return [
        ...prev,
        {
          id: materialId,
          quantity: amount,
          material: materials.find((m) => m.id === materialId)
        },
      ];
    });
  };

  useEffect(() => {
    // console.log(selectedMaterials);
  }, [selectedMaterials]);

  const removeMaterialFromList = (id: number) => {
    setSelectedMaterials((prev) => prev.filter((item) => item.id !== id));
  };

  const onSubmit = (data: AddQuoteItemDto) => {
    const quoteItemData: AddQuoteItemDto = {
      ...data,
      description: description,
      materials: selectedMaterials,
    };

    quoteItem ? onUpdate(quoteItem.id, quoteItemData) : onSave(quoteItemData);
  };


  const renderedMaterials = Object.entries(selectedMaterials).map((currentMaterial) => {
    const {id, quantity, material} = currentMaterial[1];

    return {
      id: id,
      name: material?.name ?? "Onbekend",
      netPrice: Number(material?.netPrice) ?? 0,
      quantity,
      total: Number(Number(material?.netPrice) * quantity),
    };
  });

  return (
    <StyledQuoteItemForm onSubmit={handleSubmit(onSubmit)}>
      <Box className="row">
        <Box className="column">
          <Typography variant="h4">
            {documentDictionary.quote.quoteItemForm.description}
          </Typography>
          <TextArea
            className="textArea"
            setValue1={setDescription}
            {...register("description", {
              required: false,
            })}
            InputLabelProps={{ shrink: true }}
            multiline
            InputProps={{
              rows: 4,
            }}
            fullWidth
            textColor={false}
            link={false}
            textSize={false}
            value={quoteItem?.description}
          />
        </Box>
        <Box className="column">
          <Typography variant="h4">
            {documentDictionary.quote.quoteItemForm.description}
          </Typography>
          <Controller
            name="includeInTotal"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                label="Include in total"
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
              />
            )}
          />
        </Box>
      </Box>
      <Box className="row">
        <Box className="column">
          <Typography variant="h4">
            {documentDictionary.quote.quoteItemForm.plannedHours}
          </Typography>
          <TextField
            type="number"
            {...register("plannedHours", {
              required: dictionary.errormessages.form_fieldrequired,
            })}
            error={!!errors.description}
            helperText={errors?.description?.message}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>
        <Box className="column">
          <Typography variant="h4">
            {documentDictionary.quote.quoteItemForm.hourRate}
          </Typography>
          <TextField
            type="number"
            {...register("hourRate", {
              required: dictionary.errormessages.form_fieldrequired,
            })}
            error={!!errors.description}
            helperText={errors?.description?.message}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>
        <Box className="column">
          <Typography variant="h4">
            {documentDictionary.quote.quoteItemForm.materialPercentage}
          </Typography>
          <TextField
            type="number"
            {...register("materialPercentage", {
              required: dictionary.errormessages.form_fieldrequired,
            })}
            error={!!errors.materialPercentage}
            helperText={errors?.materialPercentage?.message}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            fullWidth
          />
        </Box>

        <Box className="column">
          <Typography variant="h4">
            {documentDictionary.quote.quoteItemForm.vat}
          </Typography>
          <TextField
            type="number"
            {...register("vat", {
              required: dictionary.errormessages.form_fieldrequired,
            })}
            error={!!errors.vat}
            helperText={errors?.vat?.message}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            fullWidth
          />
        </Box>
        <Box className="column">
          <Typography variant="h4">
            {documentDictionary.quote.quoteItemForm.price}
          </Typography>
          <TextField
            type="number"
            {...register("extraAmount", {
              required: false,
            })}
            error={!!errors.description}
            helperText={errors?.description?.message}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>
      </Box>
      <Box className="row">
        <Box className="column">
          <Typography variant="h4">
            {documentDictionary.quote.quoteItemForm.materials}
          </Typography>
          <Box className="row">
            <Box className="column">
              <Autocomplete
                options={materials}
                fullWidth
                className="select"
                getOptionLabel={(option) => `${option.brand} - ${option.name}`}
                onChange={(_, newValue) => setCurrentMaterial(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Select Material" />
                )}
              />
            </Box>
            <Box className="column">
              <TextField
                type="number"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
              />
            </Box>
            <Box className="column">
              <Button onClick={handleAddMaterial}>
                <PlusIcon size={32} weight="bold" />
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box className="row">
        <Box className="column">
          <Box className="materialList">
            <Box className="materials header">
              <Typography variant="h4" className="pos">
                Pos
              </Typography>
              <Typography variant="h4" className="material">
                Materiaal
              </Typography>
              <Typography variant="h4" className="pricepiece">
                Prijs/eenh.
              </Typography>
              <Typography variant="h4" className="amount">
                Aantal
              </Typography>
              <Typography variant="h4" className="total">
                Totaal
              </Typography>
              <Typography variant="h4" className="actions">
                Acties
              </Typography>
            </Box>
            {renderedMaterials.map((mat, index) => (
              <Box key={mat.id} className="materials">
                <Typography variant="h4" className="pos">
                  {index + 1}
                </Typography>

                <Typography variant="h4" className="material">
                  {mat.name}
                </Typography>

                <Typography variant="h4" className="pricepiece">
                  €{mat.netPrice.toFixed(2)}
                </Typography>

                <Typography variant="h4" className="amount">
                  {Number(mat.quantity)}
                </Typography>

                <Typography variant="h4" className="total">
                  €{Number(mat.total).toFixed(2)}
                </Typography>

                <Typography variant="h4" className="actions">
                  <TrashSimpleIcon
                    size={24}
                    weight="bold"
                    onClick={() => removeMaterialFromList(mat.id)}
                  />
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box className="row">
        <Button type="submit">Opslaan</Button>
      </Box>
    </StyledQuoteItemForm>
  );
};
